import { Capacitor } from '@capacitor/core'

/**
 * Single source of truth for "am I running inside the Capacitor native shell?".
 * Branch on this — never inspect `window.Capacitor` or read `import.meta.env.MODE`
 * directly elsewhere in the app.
 */
export const isNative = Capacitor.isNativePlatform()
export const isWeb = !isNative

export const isIOS = Capacitor.getPlatform() === 'ios'
export const isAndroid = Capacitor.getPlatform() === 'android'

/**
 * A WebKit-engined browser on the web build: Safari on macOS and every browser
 * on iOS / iPadOS, which Apple requires to use WebKit. Blink browsers (Chrome,
 * Edge, Opera, Samsung) advertise `AppleWebKit` too, hence the exclusions.
 *
 * Exists for the WebKit-specific media-session behaviour that `lib/audio.ts`
 * works around; it is not a feature-detect and must not be used for routing.
 * Always false on native — the Capacitor WebView has its own paths.
 */
export const isWebKitBrowser: boolean =
  isWeb &&
  typeof navigator !== 'undefined' &&
  /AppleWebKit/i.test(navigator.userAgent) &&
  !/Chrome|Chromium|Edg\/|OPR\/|SamsungBrowser/i.test(navigator.userAgent)

// ── Session App Dock ────────────────────────────────────────────────────────

export type DockRole = 'leader' | 'follower'

const DOCK_ROLE_KEY = 'lt.dock.role'

function readStoredDockRole(): DockRole | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(DOCK_ROLE_KEY)
    return raw === 'leader' || raw === 'follower' ? raw : null
  } catch {
    return null // storage unavailable (private mode / SSR)
  }
}

/**
 * Running inside a Videosprechstunde's app dock rather than standalone or in
 * the portal embed.
 *
 * Decided once, at module load, from the entry URL — the VSS always frames
 * `/dock`, and SPA navigation afterwards does not reload this module, so a
 * leader that has navigated on to `/training` is still docked. The
 * sessionStorage latch covers the remaining case: an in-frame reload, which
 * lands on the *current* path rather than back on `/dock`.
 *
 * This must never be derived from `document.referrer` or `ancestorOrigins` —
 * that would let a framing page decide what we believe about ourselves. The
 * entry path is ours; the framing origin is not.
 */
export const isDocked: boolean =
  (typeof location !== 'undefined' && location.pathname.startsWith('/dock')) ||
  readStoredDockRole() !== null

let dockedRole: DockRole | null = readStoredDockRole()

/**
 * Latch the role the shell assigned us. Called once, by `useDockBridge` on
 * `dock:hello`, and never again — the role cannot change within a dock session,
 * and code that reads it (the router guard, the API client) needs a synchronous
 * answer rather than a promise.
 */
export function markDockRole(role: DockRole): void {
  dockedRole = role
  try {
    globalThis.sessionStorage?.setItem(DOCK_ROLE_KEY, role)
  } catch {
    /* in-memory only */
  }
}

/** Our role in the dock, or null when not docked / before the handshake. */
export function dockRole(): DockRole | null {
  return dockedRole
}

/**
 * The patient's side of a dock. Holds no credentials, claims no licence and
 * talks to no backend: everything it shows arrives over the peer channel.
 * Fail-closed — an unknown role is treated as the follower, because granting
 * leader powers on a guess is the failure that matters.
 */
export function isDockFollower(): boolean {
  return isDocked && dockedRole !== 'leader'
}

/** The therapist's side of a dock: authenticated, and the only side that acts. */
export function isDockLeader(): boolean {
  return isDocked && dockedRole === 'leader'
}
